import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import { ArrowRight, MapPin, Users, Clock, KeyRound, DoorClosed, Sparkles, Gift, Camera, User } from "lucide-react";
import { api, formatDetail } from "@/lib/api";
import PublicNav from "@/components/PublicNav";

const FEATURES = [
  { icon: Sparkles, label: "themed decor" },
  { icon: Gift, label: "special gifts" },
  { icon: KeyRound, label: "lucky draw" },
  { icon: Camera, label: "photobooth" },
  { icon: DoorClosed, label: "freebies" },
];

export default function Landing() {
  const nav = useNavigate();
  const [event, setEvent] = useState(null);
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState({ name: "", people_count: 1 });
  const [nameAvailable, setNameAvailable] = useState(true);
  const [checkingName, setCheckingName] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/event").then((r) => setEvent(r.data)).catch(() => {});
    const load = () => api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) {
      toast.error("Please fill in nickname");
      return;
    }
    // Ensure name is available before submitting (fresh check)
    const ok = await checkName(form.name);
    if (!ok) {
      toast.error("Nickname is duplicate, please use another nickname");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/queue/register", { name: form.name, people_count: form.people_count });
      localStorage.setItem("okay_last_guest", data.id);
      // Ask for notification permission opportunistically
      if ("Notification" in window && Notification.permission === "default") {
        try { Notification.requestPermission(); } catch { /* no-op */ }
      }
      toast.success(`You're in the queue — number ${data.queue_number}`);
      nav(`/queue/${data.id}`);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Could not join queue");
    } finally {
      setSubmitting(false);
    }
  };

  const checkName = async (name) => {
    const v = name?.trim();
    if (!v) return setNameAvailable(true);
    setCheckingName(true);
    try {
      const { data } = await api.get("/queue/check-name", { params: { name: v } });
      setNameAvailable(Boolean(data.ok));
      return Boolean(data.ok);
    } catch (err) {
      // treat errors as name unavailable to be safe
      setNameAvailable(false);
      return false;
    } finally {
      setCheckingName(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-center" richColors />
      <PublicNav />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grain ripple" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-24 lg:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="label-eyebrow"
              data-testid="hero-eyebrow"
            >
              {event?.date || "August 1, 2026"} · {event?.time || "11:00 AM – 7:30 PM"}
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.05 }}
              className="font-display text-6xl sm:text-7xl lg:text-[8.5rem] leading-[0.9] tracking-tight mt-6 text-foreground"
              data-testid="hero-title"
            >
              Okay
              <br />
              <em className="italic font-light text-[hsl(var(--primary))]">Room</em>
              <span className="inline-block align-top ml-3 w-8 h-8 lg:w-10 lg:h-10 -rotate-12 bg-center bg-contain bg-no-repeat opacity-90" style={{ backgroundImage: "url('/hero-arrow.jpg')" }} aria-hidden />
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="mt-6 text-lg text-muted-foreground max-w-xl"
              data-testid="hero-subtitle"
            >
              {event?.subtitle || "for Young K 2nd full album: YOUNGEST"}. A limited-capacity
              café takeover — join the queue below and keep this tab open to get pinged when a seat opens.
            </motion.p>

            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm">
              <span className="flex items-center gap-2" data-testid="hero-venue">
                <MapPin className="w-4 h-4" strokeWidth={1.4} />
                {event?.venue || "Fleur by Raja Cafe, Melawai, Jakarta"}
              </span>
            </div>
          </div>

          {/* Register card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:col-span-5"
          >
            <div className="relative bg-card border border-border p-8 lg:p-10 shadow-[0_1px_0_hsl(var(--border))]">
              <div className="absolute -top-3 left-8 bg-background px-3 label-eyebrow">
                join the queue
              </div>
              <form onSubmit={onSubmit} className="space-y-5" data-testid="register-form">
                <FormField
                  label="Nickname"
                  value={form.name}
                  onChange={(v) => {
                    setForm({ ...form, name: v });
                    setNameAvailable(true);
                  }}
                  onBlur={() => checkName(form.name)}
                  placeholder="YoungK"
                  tid="input-name"
                />
                {!nameAvailable && (
                  <div className="text-xs text-rose-500">Nickname already in queue</div>
                )}
                <label className="block">
                  <span className="label-eyebrow">Total guest</span>
                  <div className="mt-2 flex items-center gap-2 border-b border-border py-2 focus-within:border-foreground transition-colors">
                    <User className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                    <select
                      value={form.people_count}
                      onChange={(e) => setForm({ ...form, people_count: parseInt(e.target.value) })}
                      data-testid="input-people"
                      className="flex-1 bg-transparent focus:outline-none text-base appearance-none cursor-pointer"
                    >
                      {Array.from({ length: 10 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>{i + 1}</option>
                      ))}
                    </select>
                  </div>
                </label>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="register-submit-btn"
                  className="group w-full mt-4 inline-flex items-center justify-between px-5 py-4 bg-foreground text-background hover:bg-[hsl(var(--primary))] transition-colors disabled:opacity-60"
                >
                  <span className="text-sm tracking-widest uppercase font-semibold">
                    {submitting ? "Joining..." : "Reserve my spot"}
                  </span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
                </button>
                <p className="text-xs text-muted-foreground pt-2">
                  Already registered?{" "}
                  <button
                    type="button"
                    data-testid="check-existing-btn"
                    onClick={() => {
                      const last = localStorage.getItem("okay_last_guest");
                      if (last) nav(`/queue/${last}`);
                      else nav("/status");
                    }}
                    className="link-underline text-foreground"
                  >
                    Check your queue
                  </button>
                </p>
              </form>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURES STRIP */}
      <section className="border-y border-border bg-[hsl(var(--accent))]/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 flex flex-wrap items-center justify-between gap-6">
          {FEATURES.map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-2 text-sm">
              <Icon className="w-4 h-4" strokeWidth={1.4} />
              <span className="lowercase tracking-wide">{label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="label-eyebrow">how it works</p>
            <h2 className="font-display text-4xl lg:text-5xl mt-4 leading-tight">
              Join the queue in just three simple steps.
            </h2>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-8">
            {[
              {
                n: "01",
                t: "Register",
                d: "Drop your nickname. You'll get a queue number and a shareable card instantly.",
              },
              {
                n: "02",
                t: "Keep tab open",
                d: "Your status page pings you (on-screen + browser notification) the moment staff calls you in.",
              },
              {
                n: "03",
                t: "Enjoy 30 min",
                d: "Explore the Okay Room, take photos, grab freebies. Timer starts when staff checks you in.",
              },
            ].map((s) => (
              <div key={s.n} className="border-t border-foreground pt-6">
                <span className="font-mono-num text-xs text-muted-foreground">{s.n}</span>
                <h3 className="font-display text-2xl mt-4">{s.t}</h3>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 text-xs text-muted-foreground flex flex-wrap justify-between gap-4">
          <span>© {new Date().getFullYear()} Okay Room · A single-day café takeover.</span>
        </div>
      </footer>
    </div>
  );
}

function Stat({ label, value, tid }) {
  return (
    <div className="px-2">
      <div className="font-mono-num text-3xl" data-testid={tid}>
        {value}
      </div>
      <div className="label-eyebrow mt-1 text-[0.6rem]">{label}</div>
    </div>
  );
}

function FormField({ label, value, onChange, placeholder, type = "text", tid, hint, prefix, onBlur }) {
  return (
    <label className="block">
      <span className="label-eyebrow">{label}</span>
      <div className="mt-2 flex items-center gap-2 border-b border-border py-2 focus-within:border-foreground transition-colors">
        {prefix}
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder}
          data-testid={tid}
          className="flex-1 bg-transparent focus:outline-none text-base"
        />
      </div>
      {hint && <span className="block text-xs text-muted-foreground mt-1.5">{hint}</span>}
    </label>
  );
}
